<template>
  <div>
    <div v-if="!configReady" class="text-xs-center">
      <v-progress-circular
        :size="70"
        :width="7"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
    <v-form>
      <v-card :class="formConfig.class">
        <v-card-actions v-if="showHeaderActions">
          <v-btn v-if="dismissButton" @click="$emit('dismiss')">
            {{ $t(dismissText) }}
          </v-btn>

          <v-spacer></v-spacer>
          <v-btn v-if="submitButton" :loading="savingData" @click="submit()">
            {{ $t(submitText) }}
          </v-btn>
        </v-card-actions>
        <v-card-title v-if="!!formTitle">
          <h3 class="headline my-0">{{ $t(formTitle) }}</h3>
        </v-card-title>
        <div v-for="(group, i) in formConfig.groups" :key="i">
          <template v-if="group.type !== 'repeater' && group.type !== 'repeater-no-card'">
            <v-card-title v-if="group.title">
              <h3 class="headline my-0">{{ $t(group.title) }}</h3>
            </v-card-title>
            <v-divider v-if="group.title" light></v-divider>
            <v-card-text>
              <aru-auto-group-field
                :disabled="disabled"
                ref="aru-auto-group-field"
                :default-field-class="defaultFieldClass"
                v-model="formData"
                :field-group="group"
              ></aru-auto-group-field>
            </v-card-text>
          </template>
          <template v-if="group.type === 'repeater'">
            <v-card
              elevation="10"
              hover
              class="ma-3"
              v-for="(groupData, j) in getFormDataToRepeater(group)"
              :key="'repeater-' + group.name + '-' + j"
            >
              <v-toolbar light color="white" dense card>
                <v-toolbar-title v-if="group.title">
                  {{ $t(group.title) }}
                </v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn
                  color="error"
                  :disabled="disabled"
                  v-if="group.repeaterMin && group.repeaterMin < j + 1"
                  flat
                  @click="removeRepeater(group.name, j)"
                >
                  <v-icon>mdi-delete</v-icon>
                  {{ getRepeaterDeleteText(group) }}
                </v-btn>
              </v-toolbar>

              <v-card-text>
                <aru-auto-group-field
                  :disabled="disabled"
                  :key="'repeater-group-' + group.name + '-' + j"
                  ref="aru-auto-group-field"
                  :default-field-class="defaultFieldClass"
                  v-model="formData[group.name][j]"
                  :field-group="group"
                ></aru-auto-group-field>
              </v-card-text>
            </v-card>

            <div class="text-xs-right">
              <v-btn color="primary" :disabled="disabled" round @click="addNewRepeater(group.name)">
                <v-icon>mdi-plus</v-icon>
                {{ getRepeaterAddText(group) }}
              </v-btn>
            </div>
          </template>

          <template v-if="group.type === 'repeater-no-card'">
            <div
              class="repeater-no-card mx-3"
              v-for="(groupData, j) in getFormDataToRepeater(group)"
              :key="'repeater-no-card' + group.name + '-' + j"
            >
              <v-layout row>
                <v-flex xs10 class="pb-3">
                  <v-label>
                    {{ $t(group.title) }}
                  </v-label>
                </v-flex>
                <v-flex xs2 class="text-xs-right">
                  <v-btn
                    color="error"
                    :disabled="disabled"
                    v-if="group.repeaterMin && group.repeaterMin < j + 1"
                    flat
                    @click="removeRepeater(group.name, j)"
                  >
                    <v-icon>mdi-delete</v-icon>
                    {{ getRepeaterDeleteText(group) }}
                  </v-btn>
                </v-flex>
              </v-layout>

              <div>
                <aru-auto-group-field
                  :key="'repeater-no-card-group-' + group.name + '-' + j"
                  :disabled="disabled"
                  ref="aru-auto-group-field"
                  :default-field-class="defaultFieldClass"
                  v-model="formData[group.name][j]"
                  :field-group="group"
                ></aru-auto-group-field>
              </div>
            </div>

            <div class="text-xs-right">
              <v-btn color="primary" :disabled="disabled" round @click="addNewRepeater(group.name)">
                <v-icon>mdi-plus</v-icon>
                {{ getRepeaterAddText(group) }}
              </v-btn>
            </div>
          </template>
        </div>
        <v-card-actions>
          <v-btn v-if="dismissButton" @click="$emit('dismiss')">
            {{ $t(dismissText) }}
          </v-btn>

          <v-spacer></v-spacer>
          <v-btn v-if="submitButton" :loading="savingData" @click="submit()">
            {{ $t(submitText) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </div>
</template>

<script>
import apiCtrl from '@/services/api/abstract/abstract-api.js';
import AruAutoGroupField from './AruAutoGroupField';
export default {
  components: {
    AruAutoGroupField,
  },
  props: {
    entity: {
      type: [String],
    },
    value: {
      type: [Object],
    },
    submitButton: {
      type: Boolean,
      default: true,
    },
    submitText: {
      type: String,
      default: 'Submit',
    },
    formConfiguration: {
      type: Object,
    },
    dismissButton: {
      type: Boolean,
      default: false,
    },
    dismissText: {
      type: String,
      default: 'dismiss',
    },
    remoteConfig: {
      type: Boolean,
      default: true,
    },
    validateBeforeSubmit: {
      type: Boolean,
      default: true,
    },
    showHeaderActions: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
    },
    savingData: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
  },
  data() {
    return {
      valid: false,
      formData: this.value,
      config: {},
      configReady: false,
    };
  },
  mounted() {
    if (this.formConfiguration) {
      this.configReady = false;
      this.config = {
        form: this.formConfiguration,
      };
      if (this.title) {
        this.config['form']['formTitle'] = this.title;
      }
      this.configReady = true;
    } else {
      this.getConfig();
    }
  },
  methods: {
    getRepeaterDeleteText(group) {
      if (group.hasOwnProperty('repeaterDeleteText')) {
        return this.$t(group.repeaterDeleteText);
      } else {
        return this.$t('Eliminar');
      }
    },

    getRepeaterAddText(group) {
      if (group.hasOwnProperty('repeaterAddText')) {
        return this.$t(group.repeaterAddText);
      } else {
        return this.$t('Añadir');
      }
    },

    getFormDataToRepeater(group) {
      if (!this.formData[group.name]) {
        let targetValue = [];
        if (this.formData.hasOwnProperty(group.name)) {
          if (Array.isArray(this.formData[group.name])) {
            for (let i = 0; i < this.formData[group.name].length; i++) {
              targetValue.push(this.formData[group.name][i]);
            }
          }
        }
        if (group.hasOwnProperty('repeaterMin') && targetValue.length < group.repeaterMin) {
          for (let i = 0; i < targetValue.length - group.repeaterMin; i++) {
            targetValue.push({});
          }
        } else if (targetValue.length === 0) {
          targetValue.push({});
        }

        this.$set(this.formData, group.name, targetValue);
      }
      return this.formData[group.name];
    },

    addNewRepeater(groupName) {
      console.log(this.formData[groupName]);
      if (!this.formData.hasOwnProperty(groupName)) {
        this.formData[groupName] = [];
      }
      this.formData[groupName].push({});
    },

    removeRepeater(groupName, i) {
      console.log('Delete ' + groupName + ' ' + i);
      console.log('before delete', this.formData[groupName]);
      this.$delete(this.formData[groupName], i);
      // delete(this.formData[groupName][i]);
      this.formData[groupName] = this.formData[groupName].filter(function() {
        return true;
      });
      this.$set(
        this.formData,
        groupName,
        this.formData[groupName].filter(function() {
          return true;
        }),
      );
      console.log('after delete', this.formData[groupName]);
    },
    getConfig() {
      apiCtrl.get('/config/' + this.entity).then(res => {
        this.config = res.data.data;
        this.configReady = true;
      });
    },
    submit() {
      // this.$validator.validateAll();
      if (this.validateBeforeSubmit) {
        console.log('validateBeforeSubmit', this.validateBeforeSubmit);
        const promises = [];
        this.$refs['aru-auto-group-field'].forEach(groupField => {
          promises.push(groupField.$validator.validateAll());
        });

        Promise.all(promises).then(
          res => {
            if (res.some(validation => validation === false)) {
              this.$emit('validationErrors', this.formData);
            } else {
              this.$emit('submit', this.formData);
            }
          },
          err => {
            this.$emit('validationErrors', this.formData);
          },
        );
      } else {
        this.$emit('submit', this.formData);
      }
    },
  },
  computed: {
    formTitle() {
      if (this.title) {
        return this.title;
      } else if (this.formConfig.title) {
        return this.formConfig.title;
      } else {
        return null;
      }
    },
    formConfig() {
      return this.configReady && this.config.hasOwnProperty('form') ? this.config['form'] : {};
    },
    defaultFieldClass() {
      if (
        this.configReady &&
        this.config.hasOwnProperty('form') &&
        this.config.form.hasOwnProperty('defaultFieldClass')
      ) {
        return this.config.form.defaultFieldClass;
      } else {
        return '';
      }
    },
  },
  watch: {
    formData: {
      handler: function(newValue) {
        this.$emit('input', this.formData);
      },
      deep: true,
    },
    value: function(val) {
      console.log(this.formData);
      // if(typeof yourVariable === 'object' && yourVariable !== null) {
      //   for (const field in this.value) {
      //     if (this.value.hasOwnProperty(field)) {
      //       const fieldData = this.value[field];

      //       this.$set(this.formData, field, fieldData );
      //     }
      //   }

      // }

      this.formData = this.value;
    },
  },
};
</script>

<style></style>
